/**
 * Theme system for dynamic color palette management
 */

import { ColorPalette, ThemeConfig } from './types';

/**
 * Apply theme colors to CSS variables
 */
export function applyTheme(theme: ThemeConfig): void {
  const root = document.documentElement;
  
  // Set CSS variables for the theme
  root.style.setProperty('--color-primary', theme.colors.primary);
  root.style.setProperty('--color-secondary', theme.colors.secondary);
  root.style.setProperty('--color-accent', theme.colors.accent);
  
  if (theme.colors.gold) {
    root.style.setProperty('--color-gold', theme.colors.gold);
  }
  
  root.style.setProperty('--color-background', theme.colors.background);
  root.style.setProperty('--color-foreground', theme.colors.foreground);
  
  if (theme.colors.muted) {
    root.style.setProperty('--color-muted', theme.colors.muted);
  }
  
  if (theme.colors.border) {
    root.style.setProperty('--color-border', theme.colors.border);
  }
  
  // Apply font families if specified
  if (theme.fontFamily?.heading) {
    root.style.setProperty('--font-heading', theme.fontFamily.heading);
  }
  
  if (theme.fontFamily?.body) {
    root.style.setProperty('--font-body', theme.fontFamily.body);
  }
}

/**
 * Get Tailwind-compatible color classes from theme
 */
export function getThemeClasses(theme: ThemeConfig) {
  return {
    primary: `text-[${theme.colors.primary}]`,
    secondary: `text-[${theme.colors.secondary}]`,
    accent: `text-[${theme.colors.accent}]`,
    gold: theme.colors.gold ? `text-[${theme.colors.gold}]` : '',
    bgPrimary: `bg-[${theme.colors.primary}]`,
    bgSecondary: `bg-[${theme.colors.secondary}]`,
    bgAccent: `bg-[${theme.colors.accent}]`,
    bgGold: theme.colors.gold ? `bg-[${theme.colors.gold}]` : '',
    border: theme.colors.border ? `border-[${theme.colors.border}]` : '',
  };
}

/**
 * Predefined color palettes
 */
export const colorPalettes: Record<string, ColorPalette> = {
  annuities: {
    primary: '#f97316', // orange-500
    secondary: '#ea580c', // orange-600
    accent: '#f97316', // orange-500
    gold: '#eab308', // yellow-500
    background: '#000000', // black
    foreground: '#ffffff', // white
    muted: '#94a3b8', // slate-400
    border: '#475569', // slate-600
  },
  blue: {
    primary: '#3b82f6', // blue-500
    secondary: '#2563eb', // blue-600
    accent: '#60a5fa', // blue-400
    background: '#ffffff',
    foreground: '#1e293b',
    muted: '#64748b',
    border: '#cbd5e1',
  },
  green: {
    primary: '#10b981', // emerald-500
    secondary: '#059669', // emerald-600
    accent: '#34d399', // emerald-400
    background: '#ffffff',
    foreground: '#1e293b',
    muted: '#64748b',
    border: '#cbd5e1',
  },
  purple: {
    primary: '#8b5cf6', // violet-500
    secondary: '#7c3aed', // violet-600
    accent: '#a78bfa', // violet-400
    background: '#ffffff',
    foreground: '#1e293b',
    muted: '#64748b',
    border: '#cbd5e1',
  },
};

/**
 * Create a theme from a color palette
 */
export function createTheme(
  name: string,
  colors: ColorPalette,
  fontFamily?: { heading?: string; body?: string }
): ThemeConfig {
  return {
    name,
    colors,
    fontFamily,
  };
}
